import { CheckCircleOutlined, TuneOutlined } from "@mui/icons-material";
import { alpha, Box, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { AccentIconTile } from "../../../../../components/UIToolkit/molecules/AccentIconTile";
import { InlineHint } from "../../../../../components/UIToolkit/molecules/InlineHint";
import { SystemBanner } from "../../../../../components/UIToolkit/molecules/SystemBanner";
import { type StatusChecklistItem, SystemStatusChecklist } from "../../../../../components/UIToolkit/molecules/SystemStatusChecklist";
import SystemButton from "../../../../../components/UIToolkit/SystemButton/SystemButton";
import { ALPHA, FONT_SIZE, LETTER_SPACING, MOTION } from "../../../../../theme/tokens";

type Props = {
	facilityName: string;
	facilityId: string;
	/** How many mapping rows the apply actually wrote. */
	writtenCount: number;
	/**
	 * Worker types the server still classifies as `unmapped` AFTER the apply — shifts that will be dropped.
	 * `null` when the proposal never loaded (Clipboard was unreachable), which must NOT be reported as
	 * "everything is mapped": claiming a clean catalog we never saw is exactly the silent failure this
	 * whole flow exists to remove.
	 */
	unmappedCount: number | null;
	/** Whether the department has an active `(clipboard, shifts)` row right now (unsaved form state included). */
	actualsOn: boolean;
	/** Jump to the manual editor — the wizard is the guided path, `SubRoleMappingGrid` stays the editor. */
	onOpenHrisMapping: () => void;
};

/**
 * The success hero: the one place in the wizard that is allowed to be loud, because it is the only
 * moment that reports an outcome rather than asking for input. A success-tinted wash + tile so the
 * result reads at a glance before any of the detail under it.
 */
const HERO_ENTRANCE_SX = {
	"@keyframes clipboardSummaryHeroEnter": {
		from: { opacity: 0, transform: "scale(0.97)" },
		to: { opacity: 1, transform: "scale(1)" },
	},
	animation: `clipboardSummaryHeroEnter ${MOTION.DURATION.ENTRANCE_MD}ms ${MOTION.EASE_OUT}`,
	"@media (prefers-reduced-motion: reduce)": { animation: "none" },
} as const;

/**
 * Step 5 — what was written, what is still missing, and the one switch the wizard deliberately did not
 * touch.
 *
 * The residual unmapped count comes from the server's own re-classification after the apply, not from
 * what the UI thinks it sent — "0 unmapped" has to be evidence, since a silently dropped shift is
 * exactly the failure this flow exists to prevent.
 */
export const ClipboardSummaryStep: FC<Props> = ({ facilityName, facilityId, writtenCount, unmappedCount, actualsOn, onOpenHrisMapping }) => {
	const { t } = useTranslation();

	const checklist: StatusChecklistItem[] = [
		{
			id: "credentials",
			label: t("department.config.clipboard.wizard.done.checklist.credentials.label"),
			description: t("department.config.clipboard.wizard.done.checklist.credentials.description"),
			state: "done",
			dataTestId: "clipboard-wizard-done-credentials",
		},
		{
			id: "facility",
			label: t("department.config.clipboard.wizard.done.checklist.facility.label"),
			description: t("department.config.clipboard.wizard.done.checklist.facility.description", { facilityId }),
			state: "done",
			dataTestId: "clipboard-wizard-done-facility",
		},
		{
			id: "roles",
			label: t("department.config.clipboard.wizard.done.checklist.roles.label", { count: writtenCount }),
			description:
				unmappedCount === null
					? t("department.config.clipboard.wizard.done.checklist.roles.unknown")
					: unmappedCount > 0
						? t("department.config.clipboard.wizard.done.checklist.roles.remaining", { count: unmappedCount })
						: t("department.config.clipboard.wizard.done.checklist.roles.allMapped"),
			state: unmappedCount === null || unmappedCount > 0 ? "blocked" : "done",
			dataTestId: "clipboard-wizard-done-roles",
		},
		{
			id: "actuals",
			label: t("department.config.clipboard.wizard.switches.actuals.label"),
			description: actualsOn ? t("department.config.clipboard.wizard.done.actuals.on") : t("department.config.clipboard.wizard.done.actuals.off"),
			state: actualsOn ? "done" : "pending",
			statusLabel: actualsOn ? t("department.config.clipboard.wizard.switches.state.on") : t("department.config.clipboard.wizard.switches.state.off"),
			dataTestId: "clipboard-wizard-done-actuals",
		},
	];

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, width: "100%" }}>
			<Box
				data-testid="clipboard-wizard-done-hero"
				sx={(theme) => ({
					...HERO_ENTRANCE_SX,
					display: "flex",
					alignItems: "center",
					gap: 2,
					p: 2.5,
					borderRadius: 2,
					backgroundColor: alpha(theme.palette.success.main, ALPHA.HERO_WASH),
					border: `1px solid ${alpha(theme.palette.success.main, ALPHA.SELECTED_OVERLAY * 2)}`,
				})}
			>
				<AccentIconTile size="lg" color="success.main" sx={(theme) => ({ backgroundColor: alpha(theme.palette.success.main, ALPHA.BRAND_TINT) })}>
					<CheckCircleOutlined />
				</AccentIconTile>
				<Box sx={{ minWidth: 0 }}>
					<Typography
						sx={{
							fontSize: FONT_SIZE.LABEL_XS,
							fontWeight: 700,
							letterSpacing: LETTER_SPACING.LABEL,
							textTransform: "uppercase",
							color: "success.dark",
						}}
					>
						{t("department.config.clipboard.wizard.done.eyebrow")}
					</Typography>
					<Typography sx={{ fontSize: FONT_SIZE.TITLE_XL, fontWeight: 700, lineHeight: 1.25 }} noWrap>
						{facilityName}
					</Typography>
					<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: "text.secondary" }}>{t("department.config.clipboard.wizard.done.subtitle")}</Typography>
				</Box>
			</Box>

			<SystemStatusChecklist
				title={t("department.config.clipboard.wizard.done.checklist.title")}
				items={checklist}
				dataTestId="clipboard-wizard-done-checklist"
			/>

			{(unmappedCount === null || unmappedCount > 0) && (
				<SystemBanner
					tone="warning"
					title={
						unmappedCount === null
							? t("department.config.clipboard.wizard.done.unmapped.unknownTitle")
							: t("department.config.clipboard.wizard.done.unmapped.title", { count: unmappedCount })
					}
					description={t("department.config.clipboard.wizard.done.unmapped.description")}
					action={
						<SystemButton buttonVariant="secondary" size="small" onClick={onOpenHrisMapping} data-testid="clipboard-wizard-done-open-mapping">
							{t("department.config.clipboard.wizard.done.unmapped.action")}
						</SystemButton>
					}
					dataTestId="clipboard-wizard-done-unmapped"
				/>
			)}

			{!actualsOn && (
				<InlineHint tone="info" icon={<TuneOutlined />} dataTestId="clipboard-wizard-done-actuals-hint">
					{t("department.config.clipboard.wizard.done.actuals.hint")}
				</InlineHint>
			)}
		</Box>
	);
};
